// AvisoAlertas.js — Aviso en pantalla cuando una acción del alumno crea una alerta
// (lección completada, módulo, avance, evaluación). Las alertas llegan en la misma
// respuesta que guarda el avance (`_alertas`), así que aparecen al instante sin
// consultar nada más; api.js las reparte con el evento 'mentoria:alertas'.
// Va por encima de todo (también del modo Mentor, que no tiene la campana a la vista).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProject } from '../../contexts/ProjectContext';
import { alertasService } from '../../services/api';
import { tipoDe, esExterno } from './alertasComun';

const DURA = 6500;
const entra = keyframes`from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: none; }`;
const corre = keyframes`from { transform: scaleX(1); } to { transform: scaleX(0); }`;

const Pila = styled.div`
  position: fixed; z-index: 3400; right: 16px; top: calc(env(safe-area-inset-top, 0px) + 16px);
  display: flex; flex-direction: column; gap: 10px; width: min(360px, calc(100vw - 32px)); pointer-events: none;
  @media (max-width: 600px) { right: 12px; left: 12px; width: auto; top: calc(env(safe-area-inset-top, 0px) + 10px); }
`;
const Tarjeta = styled.div`
  pointer-events: auto; position: relative; overflow: hidden; display: grid; grid-template-columns: 36px 1fr auto; gap: .75rem; align-items: start;
  padding: .85rem .7rem .9rem .85rem; border-radius: 14px; color: #e8ebef; text-align: left;
  background: rgba(16,18,22,.96); backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
  box-shadow: 0 0 0 1px rgba(255,255,255,.09), 0 18px 44px -14px rgba(0,0,0,.8);
  animation: ${entra} 220ms ease-out;
  @media (prefers-reduced-motion: reduce) { animation: none; }
  .ico { width: 36px; height: 36px; border-radius: 10px; display: grid; place-items: center; color: ${p => p.$c}; background: ${p => p.$c}22; }
  .tit { font-size: .9rem; font-weight: 650; line-height: 1.35; padding-top: .45rem; }
  .msg { margin-top: .2rem; font-size: .8rem; line-height: 1.4; color: #aab2bc; }
  .acc { margin-top: .45rem; border: 0; background: none; padding: 0; font: inherit; font-size: .82rem; font-weight: 650; color: ${p => p.$c}; cursor: pointer; }
  .acc:hover { text-decoration: underline; }
  .x { border: 0; background: none; color: #8b949e; width: 28px; height: 28px; border-radius: 8px; font-size: 18px; line-height: 1; cursor: pointer; }
  .x:hover { background: rgba(255,255,255,.08); color: #fff; }
  button:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
`;
// Lo que falta para que se cierre solo; se detiene con el cursor encima.
const Tiempo = styled.span`
  position: absolute; left: 0; right: 0; bottom: 0; height: 2px; background: ${p => p.$c}; opacity: .7; transform-origin: left;
  ${p => css`animation: ${corre} ${DURA}ms linear forwards;`}
  animation-play-state: ${p => p.$pausa ? 'paused' : 'running'};
  @media (prefers-reduced-motion: reduce) { display: none; }
`;

const Aviso = ({ a, onCerrar, onIr, mostrarAccion }) => {
  const [pausa, setPausa] = useState(false);
  const t = tipoDe(a.tipo);
  return (
    <Tarjeta $c={t.color} onMouseEnter={() => setPausa(true)} onMouseLeave={() => setPausa(false)}
      onFocus={() => setPausa(true)} onBlur={() => setPausa(false)}>
      <span className="ico">{t.icono}</span>
      <div>
        <div className="tit">{a.titulo}</div>
        {a.mensaje && <div className="msg">{a.mensaje}</div>}
        {mostrarAccion && <button type="button" className="acc" onClick={() => onIr(a)}>{a.texto_enlace || 'Ver'}</button>}
      </div>
      <button type="button" className="x" aria-label="Cerrar aviso" onClick={() => onCerrar(a.id)}>×</button>
      <Tiempo $c={t.color} $pausa={pausa} onAnimationEnd={() => onCerrar(a.id)} />
    </Tarjeta>
  );
};

const AvisoAlertas = () => {
  const { proyecto } = useProject();
  const [avisos, setAvisos] = useState([]);
  const vistos = useRef(new Set());
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pid = proyecto?.id || null;
  // El sistema de alertas viene apagado: solo donde el admin lo activa.
  const apagada = proyecto?.config_json?.alertas?.activo !== true;

  useEffect(() => {
    if (apagada) return undefined;
    const al = (e) => {
      const nuevas = (e.detail?.nuevas || []).filter(a => !vistos.current.has(a.id)
        && (!a.proyecto_id || !pid || Number(a.proyecto_id) === Number(pid)));
      if (!nuevas.length) return;
      nuevas.forEach(a => vistos.current.add(a.id));
      setAvisos(prev => [...nuevas, ...prev].slice(0, 3));
    };
    window.addEventListener('mentoria:alertas', al);
    return () => window.removeEventListener('mentoria:alertas', al);
  }, [pid, apagada]);

  const cerrar = useCallback((id) => setAvisos(prev => prev.filter(a => a.id !== id)), []);
  const ir = useCallback((a) => {
    cerrar(a.id);
    if (pid) alertasService.leer(pid, [a.id]).catch(() => {});
    if (esExterno(a.enlace)) window.open(a.enlace, '_blank', 'noopener');
    else navigate(a.enlace);
  }, [cerrar, navigate, pid]);

  if (!avisos.length) return null;
  // En la misma pantalla (p. ej. la siguiente lección del curso abierto) el reproductor
  // ya tiene su propio «Siguiente»: el aviso solo informa.
  const otraPantalla = (a) => a.enlace && (esExterno(a.enlace) || a.enlace.split('?')[0] !== pathname);
  return createPortal(
    <Pila aria-live="polite">
      {avisos.map(a => <Aviso key={a.id} a={a} onCerrar={cerrar} onIr={ir} mostrarAccion={otraPantalla(a)} />)}
    </Pila>,
    document.body
  );
};

export default AvisoAlertas;
